import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/api/queries";
import type {
  DropshipV2Field,
  DropshipV2Issue,
  DropshipV2Mutation,
  DropshipV2Shell,
} from "./types";

export type V2RequestErrorBody = {
  detail?: string | { code?: string; message?: string };
};

export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  const detail = (error as V2RequestErrorBody | undefined)?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || detail?.code?.replaceAll("_", " ") || fallback;
}

export function mergeIssueDelta(
  current: DropshipV2Issue[] | undefined,
  delta: DropshipV2Issue[],
) {
  const byId = new Map((current ?? []).map((issue) => [issue.id, issue]));
  for (const issue of delta) {
    if (issue.status === "open") byId.set(issue.id, issue);
    else byId.delete(issue.id);
  }
  return [...byId.values()].sort(
    (left, right) =>
      Number(right.blocking) - Number(left.blocking) || left.id - right.id,
  );
}

export function mergeFields(
  current: DropshipV2Field[] | undefined,
  changed: DropshipV2Field[],
): DropshipV2Field[] | undefined {
  if (!current) return current;
  const byUuid = new Map(changed.map((field) => [field.uuid, field]));
  return current.map((field) => byUuid.get(field.uuid) ?? field);
}

export function refreshOperationalViews(
  queryClient: QueryClient,
  draftUuid?: string,
) {
  if (draftUuid) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.audit(draftUuid),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.dropshipV2.freightReview(draftUuid),
    });
  }
  void queryClient.invalidateQueries({
    queryKey: ["dropship-v2", "work-queue"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["dropship-v2", "readiness-metrics"],
  });
}

export async function applyMutationToShell(
  queryClient: QueryClient,
  draftUuid: string,
  mutation: Pick<DropshipV2Mutation, "content_revision" | "invalidated_plan">,
  patch?: (shell: DropshipV2Shell) => Partial<DropshipV2Shell>,
) {
  await queryClient.cancelQueries({
    queryKey: queryKeys.dropshipV2.shell(draftUuid),
    exact: true,
  });
  queryClient.setQueryData<DropshipV2Shell>(
    queryKeys.dropshipV2.shell(draftUuid),
    (shell) =>
      shell
        ? {
            ...shell,
            ...patch?.(shell),
            content_revision: Math.max(
              shell.content_revision,
              mutation.content_revision,
            ),
            current_po_plan_id: mutation.invalidated_plan
              ? null
              : shell.current_po_plan_id,
          }
        : shell,
  );
  if (mutation.invalidated_plan) {
    queryClient.removeQueries({
      queryKey: queryKeys.dropshipV2.plan(draftUuid),
    });
  }
}
